import React from 'react';
import { Card, CardContent, Typography, Box, Skeleton, Tooltip, IconButton } from '@mui/material';
import { styled } from '@mui/material/styles';
import HelpOutlineOutlinedIcon from '@mui/icons-material/HelpOutlineOutlined';

const StyledCard = styled(Card)(({ theme }) => ({
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  borderRadius: theme.shape.borderRadius,
  boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
  '&:hover': {
    boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
  },
}));

const ValueTypography = styled(Typography)(({ theme, color }) => ({
  fontWeight: 700,
  fontSize: '1.5rem',
  color: color === 'error' ? theme.palette.error.main : theme.palette.text.primary,
}));

interface SummaryCardProps {
  title: string;
  value: string | number;
  unit?: string;
  isNegative?: boolean;
  loading?: boolean;
  tooltip?: string;
}

const SummaryCard: React.FC<SummaryCardProps> = ({
  title,
  value,
  unit,
  isNegative = false,
  loading = false,
  tooltip,
}) => {
  const displayValue = typeof value === 'number' ? value.toFixed(2) : value;
  const colorProp = isNegative && typeof value === 'number' && value > 0 ? 'error' : undefined;

  return (
    <StyledCard>
      <CardContent sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ fontWeight: 500 }}
          >
            {title}
          </Typography>
          {tooltip && (
            <Tooltip title={tooltip} arrow placement="top">
              <IconButton size="small" sx={{ p: 0.25 }}>
                <HelpOutlineOutlinedIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5 }}>
          {loading ? (
            <>
              <Skeleton variant="text" width={100} height={32} />
              {unit && (
                <Skeleton variant="text" width={40} height={24} />
              )}
            </>
          ) : (
            <>
              <ValueTypography color={colorProp}>
                {displayValue}
              </ValueTypography>
              {unit && (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ fontWeight: 500 }}
                >
                  {unit}
                </Typography>
              )}
            </>
          )}
        </Box>
      </CardContent>
    </StyledCard>
  );
};

export default SummaryCard;
